// A scan's pose graph (prep/links.py: RTAB-Map's links between its frames) against the frames as the
// editor places them: how far each link is stretched from what RTAB-Map measured, for drawing the scan
// path; and the links as springs, for carrying a frame's neighbours along when it is aligned, and for
// pulling frames together where they share a named point (relax).
import { type Rigid, apply, compose, identity, inverse } from '../align.ts';
import type { OrigLinks } from '../format.ts';
import type { V3 } from '../math.ts';

export interface Link { a: number; b: number; type: number; wt: number; wr: number; M: Rigid }
export interface PoseGraph { frames: number[]; pose: Map<number, Rigid>; links: Link[] }

const rigid12 = (x: ArrayLike<number>, o = 0): Rigid => ({
  R: [x[o], x[o + 1], x[o + 2], x[o + 4], x[o + 5], x[o + 6], x[o + 8], x[o + 9], x[o + 10]], t: [x[o + 3], x[o + 7], x[o + 11]],
});

/** The graph of a links document (poses in the scan's file frame). */
export function graphOf(d: OrigLinks): PoseGraph {
  const pose = new Map(d.frames.map((f, i) => [f, rigid12(d.pose[i])]));
  const links = d.links.filter(l => pose.has(l[0]) && pose.has(l[1]))
    .map(l => ({ a: l[0], b: l[1], type: l[2], wt: l[3], wr: l[4], M: rigid12(l, 5) }));
  return { frames: d.frames, pose, links };
}

/** The angle (radians) of a rotation, accurate near 0 (where acos of the trace is not). */
export function angleOf(R: number[]) {
  const s = Math.hypot(R[7] - R[5], R[2] - R[6], R[3] - R[1]) / 2, c = (R[0] + R[4] + R[8] - 1) / 2;
  return Math.atan2(s, c);
}

/** How far a link is from its measurement: dt (m, in a's frame) and dr (radians), with E the frames' tree transforms. */
export function linkStretch(g: PoseGraph, l: Link, E: (f: number) => Rigid) {
  const A = compose(E(l.a), g.pose.get(l.a)!), B = compose(E(l.b), g.pose.get(l.b)!), rel = compose(inverse(A), B);
  const dt = Math.hypot(rel.t[0] - l.M.t[0], rel.t[1] - l.M.t[1], rel.t[2] - l.M.t[2]);
  return { dt, dr: angleOf(compose(inverse(l.M), rel).R) };
}

/** A stretch on one scale: 1 per 2 cm or per degree, whichever is more. */
export const stretchScale = (dt: number, dr: number) => Math.max(dt / 0.02, (dr * 180) / Math.PI);

/** Green (none) → yellow (~2 cm / 1°) → red (10 cm / 5° and more), RGB 0-1. */
export function stretchColor(s: number): [number, number, number] {
  const t = Math.min(1, Math.log1p(s) / Math.log(6));
  return t < 0.4 ? [0.2 + 2 * t, 0.85, 0.3 - 0.5 * t] : [1, 0.85 - 1.08 * (t - 0.4), 0.1];
}

/** Frames that share a named point: each member's frame, where its point is in the world now, the offset (world)
 * its point should meet the others' at, and its weight (how much its frame gives: 1 as much as any, 0 not at all). */
export type Tie = { f: number; q: V3; o?: V3; w?: number }[];

/** The pose graphs of several scans as one (frames renumbered 1, 2, …), so ties can join frames of different scans. */
export function joinGraphs<K>(parts: { key: K; g: PoseGraph | null; place: (f: number) => Rigid; frames?: Iterable<number> }[]) {
  const ids = new Map<K, Map<number, number>>(), back: { key: K; f: number; place: (f: number) => Rigid }[] = [];
  const frames: number[] = [], pose = new Map<number, Rigid>(), links: Link[] = [];
  for (const x of parts) {
    const m = new Map<number, number>();
    ids.set(x.key, m);
    for (const f of [...(x.g?.frames ?? []), ...(x.frames ?? [])]) {
      if (m.has(f)) continue;
      back.push({ key: x.key, f, place: x.place });
      m.set(f, back.length); frames.push(back.length); pose.set(back.length, x.g?.pose.get(f) ?? identity());
    }
    for (const l of x.g?.links ?? []) links.push({ ...l, a: m.get(l.a)!, b: m.get(l.b)! });
  }
  return {
    g: { frames, pose, links } as PoseGraph,
    place: (i: number) => { const b = back[i - 1]; return b.place(b.f); },
    id: (key: K, f: number) => ids.get(key)?.get(f),
    of: (i: number) => ({ key: back[i - 1].key, f: back[i - 1].f }),
  };
}

/** The widest gap between the points of a tie, with each frame moved by move(f) (none: where it is). */
export function tieGap(tie: Tie, move: (f: number) => Rigid | undefined = () => undefined) {
  const q = tie.map(m => { const W = move(m.f), q = W ? apply(W, m.q) : m.q, o = m.o ?? [0, 0, 0]; return [q[0] + o[0], q[1] + o[1], q[2] + o[2]]; });
  let d = 0;
  for (let i = 0; i < q.length; i++) for (let j = i + 1; j < q.length; j++) d = Math.max(d, Math.hypot(q[i][0] - q[j][0], q[i][1] - q[j][1], q[i][2] - q[j][2]));
  return d;
}

/** The rotation vector (axis · angle, radians) of R. */
export function rotvec(R: number[]): V3 {
  const a = angleOf(R), v: V3 = [R[7] - R[5], R[2] - R[6], R[3] - R[1]], s = Math.hypot(...v);
  if (s > 1e-9) return [(v[0] / s) * a, (v[1] / s) * a, (v[2] / s) * a];
  if (a < 1e-6) return [0, 0, 0];
  // a half turn: the axis from the diagonal
  const i = R[0] >= R[4] && R[0] >= R[8] ? 0 : R[4] >= R[8] ? 1 : 2, k: V3 = [0, 0, 0];
  k[i] = Math.sqrt(Math.max(0, (R[4 * i] + 1) / 2));
  for (const j of [0, 1, 2]) if (j !== i) k[j] = (R[3 * i + j] + R[3 * j + i]) / (4 * k[i]);
  return [k[0] * a, k[1] * a, k[2] * a];
}

/** The rotation of a rotation vector w (Rodrigues). */
export function rotOf(w: ArrayLike<number>): number[] {
  const a = Math.hypot(w[0], w[1], w[2]);
  if (a < 1e-15) return identity().R;
  const x = w[0] / a, y = w[1] / a, z = w[2] / a, c = Math.cos(a), s = Math.sin(a), C = 1 - c;
  return [c + x * x * C, x * y * C - z * s, x * z * C + y * s, y * x * C + z * s, c + y * y * C, y * z * C - x * s, z * x * C - y * s, z * y * C + x * s, c + z * z * C];
}

/** How far R tips the vertical: the turn (x, y of its vector, radians) about a level axis that takes up to R·up. */
export function tiltOf(R: number[]): [number, number] {
  const ux = R[2], uy = R[5], uz = R[8], h = Math.hypot(ux, uy);
  if (h < 1e-15) return [0, 0];
  const a = Math.atan2(h, uz);
  return [(-uy / h) * a, (ux / h) * a];
}

/** The distance (m) out from a frame's pose at which its tether holds a turn as it holds a shift. */
const TURN_LEVER = 2;

/** A turn by R about p: R x + (p − R p). */
const about = (R: number[], p: V3): Rigid => { const q = apply({ R, t: [0, 0, 0] }, p); return { R, t: [p[0] - q[0], p[1] - q[1], p[2] - q[2]] }; };

/**
 * Pitch and roll for relax. max: how far (radians) a frame may tilt from where RTAB-Map had it; edited(f): the
 * world turn the editor has put on the frame on top of RTAB-Map's (none: it is where RTAB-Map had it). capped
 * collects the frames held at max.
 */
export interface TiltOpts { max: number; edited?: (f: number) => Rigid; capped?: Set<number> }

/**
 * The links as springs: world corrections for the free frames of a scan, given some frames moved by a
 * known world W (anchors: the ones just aligned), some held where they are (locked), and ties (frames whose
 * named points should meet). Each frame's correction is a turn θ about the vertical through its pose and a
 * shift t (yaw, x, y, z: the moves the frame fit makes), small enough to be linear; with tilt, also a pitch and
 * roll (a full small turn ω about its pose). A link is at rest as
 * RTAB-Map measured it: it costs wt·|r0 + Δ_b(p) − Δ_a(p)|² + wr·(ρ0 + θ_b − θ_a)² at its midpoint p, with r0, ρ0
 * how far it is stretched now (so a frame aligned back from a drift releases its links and leaves its
 * neighbours be, while one aligned away from where RTAB-Map had it pulls them along). Every free frame is
 * tethered to where it is, weakly (1/reach² of a typical link; a turn at least as much as a shift TURN_LEVER out), so a pull fades out over about `reach` links
 * instead of carrying the whole scan along; only frames within 3·reach links of an anchor or of a tie whose
 * points are apart (and the frames tied to those) are free, so stretch elsewhere (a frame moved by hand) is left as it is. reach 0: no springs, only the tied
 * frames move (as little as they can). Tilts are held to RTAB-Map's, not to where the frame is: each frame by a
 * tether 100 times its yaw's (a degree of tilt costs what 100 of turn do) and each link by wr on how much more
 * one end is tilted than the other; and no frame tilts more than tilt.max from RTAB-Map's (held there, the ties
 * it is in may not quite meet). Solved by conjugate gradients; ties are met exactly (augmented
 * Lagrangian, then a few rounds about the moved poses for the turns' second order), unless every frame of one
 * is held, or they cannot all be (one frame's named points not placed quite as another's): then as near as they come. place(f): the frame's world pose (T · E(f) · pose). Returns the world corrections of the frames that
 * move (by more than 0.1 mm / 0.001°).
 */
export function relax(g: PoseGraph, place: (f: number) => Rigid, anchors: Map<number, Rigid>, locked: Set<number>, reach = 10, ties: Tie[] = [], tilt?: TiltOpts): Map<number, Rigid> {
  const out = new Map<number, Rigid>();
  const p0 = new Map(g.frames.map(f => [f, place(f)]));
  ties = ties.map(t => t.filter(m => p0.has(m.f))).filter(t => t.length > 1);
  const springs = reach > 0;
  if (!ties.length && (!anchors.size || !springs)) return out;
  // a tied frame's weight (the least of its ties'): 0 holds it, else its tether is 1/w of a free frame's
  const wOf = new Map<number, number>();
  for (const t of ties) for (const m of t) wOf.set(m.f, Math.min(wOf.get(m.f) ?? Infinity, m.w ?? 1));
  if ([...wOf.values()].some(w => w <= 0)) locked = new Set([...locked, ...[...wOf].filter(([, w]) => w <= 0).map(([f]) => f)]);
  if (!springs) reach = 1;
  const links = springs ? g.links : [];
  // free: within 3·reach links of an anchor or a tie that is apart (breadth first; a tie's frames are as near as
  // each other, so a frame pulled drags its partners in), not locked, not an anchor
  const nb = new Map<number, number[]>(), tnb = new Map<number, number[]>();
  const edge = (to: Map<number, number[]>, a: number, b: number) => { (to.get(a) ?? to.set(a, []).get(a)!).push(b); (to.get(b) ?? to.set(b, []).get(b)!).push(a); };
  for (const l of links) edge(nb, l.a, l.b);
  for (const t of ties) for (let k = 1; k < t.length; k++) edge(tnb, t[k - 1].f, t[k].f);
  const hops = new Map([...anchors.keys(), ...ties.filter(t => tieGap(t) > 1e-4).flatMap(t => t.map(m => m.f))].filter(f => p0.has(f)).map(f => [f, 0]));
  for (let q = [...hops.keys()], k = 0; k < q.length; k++) {
    const h = hops.get(q[k])!;
    if (locked.has(q[k])) continue;
    for (const m of tnb.get(q[k]) ?? []) if (!hops.has(m)) { hops.set(m, h); q.push(m); }
    if (h >= (springs ? 3 * reach : 0)) continue;
    for (const m of nb.get(q[k]) ?? []) if (!hops.has(m)) { hops.set(m, h + 1); q.push(m); }
  }
  const free = g.frames.filter(f => hops.has(f) && !anchors.has(f) && !locked.has(f)), ix = new Map(free.map((f, i) => [f, i])), n = free.length;
  if (!n) return out;
  ties = ties.filter(t => t.some(m => hops.has(m.f)));   // the rest are met, and nothing here moves them
  const tw = Float64Array.from(free, f => 1 / Math.max(1e-3, wOf.get(f) ?? 1));
  const ok = (x: number, d: number) => (Number.isFinite(x) && x > 0 ? x : d);
  const med = (v: number[], d: number) => v.sort((x, y) => x - y)[v.length >> 1] ?? d;
  const wt0 = med(links.map(l => ok(l.wt, 1e4)), 1e4), kt = wt0 / (reach * reach), kr0 = med(links.map(l => ok(l.wr, 1e3)), 1e3) / (reach * reach), kp = 100 * kr0;
  // a turn's tether at least the shift's at TURN_LEVER: else a frame with no links to hold it would turn freely, and ties
  // that cannot quite meet would swing it a long way for a mm of their gap
  const kr = Math.max(kr0, kt * TURN_LEVER * TURN_LEVER);
  const tilts = !!tilt && tilt.max > 0, tmax = tilt?.max ?? 0;
  // a tie's points are held together by a spring μ (100 typical links) and multipliers λ that make up the rest
  const mu = 100 * wt0, pairs = ties.flatMap(t => t.slice(1).map((m, k) => ({ i: t[k], j: m }))), lam = pairs.map(() => [0, 0, 0]);
  // C: the world correction of each frame so far (the anchors' W once the first round has linearised it)
  const C = new Map<number, Rigid>();
  const moved = (f: number) => (C.has(f) ? compose(C.get(f)!, p0.get(f)!) : p0.get(f)!);
  // how far a frame is tilted from RTAB-Map's now (the editor's turn, then C's)
  const E0 = (f: number) => tilt?.edited?.(f).R ?? identity().R;
  const tauOf = (f: number): [number, number] => (tilts ? tiltOf(C.has(f) ? compose(C.get(f)!, { R: E0(f), t: [0, 0, 0] }).R : E0(f)) : [0, 0]);
  // per unknown (t, ω for each free frame): 1 if it is solved for; a frame's tilt is not without tilt, or once held at max
  const mask = new Float64Array(6 * n), held = new Map<number, [number, number]>();
  // r = J_b x_b − J_a x_a; J x = (t + ω × l, ω): the shift at l from the frame's position, and the turn
  const Jx = (x: ArrayLike<number>, o: number, l: number[], r: number[], sgn: number) => {
    const wx = x[o + 3], wy = x[o + 4], wz = x[o + 5];
    r[0] += sgn * (x[o] + wy * l[2] - wz * l[1]); r[1] += sgn * (x[o + 1] + wz * l[0] - wx * l[2]); r[2] += sgn * (x[o + 2] + wx * l[1] - wy * l[0]);
    r[3] += sgn * wx; r[4] += sgn * wy; r[5] += sgn * wz;
  };
  // Jᵀ W r: (ω × l)·r = ω·(l × r)
  const JtWr = (out: Float64Array, o: number, l: number[], r: number[], wt: number, wr: number, sgn: number) => {
    out[o] += sgn * wt * r[0]; out[o + 1] += sgn * wt * r[1]; out[o + 2] += sgn * wt * r[2];
    out[o + 3] += sgn * (wt * (l[1] * r[2] - l[2] * r[1]) + wr * r[3]);
    out[o + 4] += sgn * (wt * (l[2] * r[0] - l[0] * r[2]) + wr * r[4]);
    out[o + 5] += sgn * (wt * (l[0] * r[1] - l[1] * r[0]) + wr * r[5]);
  };
  const dot = (u: Float64Array, v: Float64Array) => { let s = 0; for (let i = 0; i < u.length; i++) s += u[i] * v[i]; return s; };
  const sub = (a: V3, b: V3) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
  const gapNow = () => Math.max(0, ...ties.map(t => tieGap(t, f => C.get(f))));
  const x = new Float64Array(6 * n);
  for (let round = 0; round < (ties.length ? 5 : 1); round++) {
    const P = new Map(g.frames.map(f => [f, moved(f)])), c = (f: number) => P.get(f)!.t, tau = new Map(g.frames.map(f => [f, tauOf(f)]));
    // fixed frames' (t, ω) about their own pose position: the anchors' W in the first round, nothing after
    const fixed = new Map<number, number[]>();
    for (const f of g.frames) if (!ix.has(f)) fixed.set(f, [0, 0, 0, 0, 0, 0]);
    if (round === 0) for (const [f, W] of anchors) {
      if (!P.has(f)) continue;
      const p = c(f), q = apply(W, p), w = rotvec(W.R);
      fixed.set(f, [q[0] - p[0], q[1] - p[1], q[2] - p[2], tilts ? w[0] : 0, tilts ? w[1] : 0, w[2]]);
    }
    const L = links.map(l => {
      const pa = c(l.a), pb = c(l.b), m: V3 = [(pa[0] + pb[0]) / 2, (pa[1] + pb[1]) / 2, (pa[2] + pb[2]) / 2];
      // its stretch now (world): where b is, less where a and the measurement put it; how much more b is tilted
      // than a (from RTAB-Map's); and the turn between them
      const B = P.get(l.b)!, pred = compose(P.get(l.a)!, l.M), Rd = compose(B, inverse(pred)).R, ta = tau.get(l.a)!, tb = tau.get(l.b)!;
      const r0 = [B.t[0] - pred.t[0], B.t[1] - pred.t[1], B.t[2] - pred.t[2], tb[0] - ta[0], tb[1] - ta[1], Math.atan2(Rd[3], Rd[0])];
      return { a: l.a, b: l.b, ia: ix.get(l.a), ib: ix.get(l.b), fa: fixed.get(l.a), fb: fixed.get(l.b), wt: ok(l.wt, 1e4), wr: ok(l.wr, 1e3), la: sub(m, pa), lb: sub(m, pb), r0 };
    });
    // the ties, a member to the next: (q_i + Δ_i(q_i)) − (q_j + Δ_j(q_j)), with the lever to each point
    const T = pairs.map(({ i, j }) => {
      const qi = C.has(i.f) ? apply(C.get(i.f)!, i.q) : i.q, qj = C.has(j.f) ? apply(C.get(j.f)!, j.q) : j.q, oi = i.o ?? [0, 0, 0], oj = j.o ?? [0, 0, 0];
      return { a: i.f, b: j.f, ia: ix.get(i.f), ib: ix.get(j.f), fa: fixed.get(i.f), fb: fixed.get(j.f), la: sub(qi, c(i.f)), lb: sub(qj, c(j.f)),
        c0: [qi[0] + oi[0] - qj[0] - oj[0], qi[1] + oi[1] - qj[1] - oj[1], qi[2] + oi[2] - qj[2] - oj[2]] };
    });
    /** A tie's residual: with x, its free members' part alone; with x null, what the rest (now, the fixed members) makes. */
    const tieR = (t: (typeof T)[number], x: Float64Array | null, r: number[]) => {
      const { ia, ib, fa, fb } = t;
      r.fill(0);
      if (x) { if (ia !== undefined) Jx(x, 6 * ia, t.la, r, 1); if (ib !== undefined) Jx(x, 6 * ib, t.lb, r, -1); }
      else { r[0] = t.c0[0]; r[1] = t.c0[1]; r[2] = t.c0[2]; if (fa) Jx(fa, 0, t.la, r, 1); if (fb) Jx(fb, 0, t.lb, r, -1); }
      r[3] = r[4] = r[5] = 0;
      return { ia, ib };
    };
    /** H x over the free unknowns; with x null, the gradient from the stretch now, the fixed frames' moves, the tilts and λ alone (the right-hand side, negated). */
    const applyH = (x: Float64Array | null, res: Float64Array) => {
      res.fill(0);
      const r = [0, 0, 0, 0, 0, 0];
      for (const l of L) {
        const { ia, ib, fa, fb } = l;
        if (x) r.fill(0); else { if (ia === undefined && ib === undefined) continue; for (let d = 0; d < 6; d++) r[d] = l.r0[d]; }
        if (x) { if (ib !== undefined) Jx(x, 6 * ib, l.lb, r, 1); if (ia !== undefined) Jx(x, 6 * ia, l.la, r, -1); }
        else { if (fb) Jx(fb, 0, l.lb, r, 1); if (fa) Jx(fa, 0, l.la, r, -1); }
        if (ib !== undefined) JtWr(res, 6 * ib, l.lb, r, l.wt, l.wr, 1);
        if (ia !== undefined) JtWr(res, 6 * ia, l.la, r, l.wt, l.wr, -1);
      }
      T.forEach((t, k) => {
        const { ia, ib } = tieR(t, x, r);
        if (ia === undefined && ib === undefined) return;
        if (!x) for (let d = 0; d < 3; d++) r[d] += lam[k][d] / mu;
        if (ia !== undefined) JtWr(res, 6 * ia, t.la, r, mu, 0, 1);
        if (ib !== undefined) JtWr(res, 6 * ib, t.lb, r, mu, 0, -1);
      });
      for (let i = 0; i < n; i++) {
        const o = 6 * i, a = kt * tw[i], p = kp * tw[i];
        if (x) {
          res[o] += a * x[o]; res[o + 1] += a * x[o + 1]; res[o + 2] += a * x[o + 2];
          res[o + 3] += p * x[o + 3]; res[o + 4] += p * x[o + 4]; res[o + 5] += kr * tw[i] * x[o + 5];
        } else { const t0 = tau.get(free[i])!; res[o + 3] += p * t0[0]; res[o + 4] += p * t0[1]; }   // the tilt's tether is to RTAB-Map's
      }
    };
    // Jacobi preconditioner: the diagonal of H
    const dg = new Float64Array(6 * n);
    for (let i = 0; i < n; i++) { const o = 6 * i; dg[o] = dg[o + 1] = dg[o + 2] = kt * tw[i]; dg[o + 3] = dg[o + 4] = kp * tw[i]; dg[o + 5] = kr * tw[i]; }
    for (const l of [...L.map(l => ({ ...l, w: l.wt })), ...T.map(t => ({ ...t, w: mu, wr: 0 }))]) for (const [i, lv] of [[l.ia, l.la], [l.ib, l.lb]] as const) {
      if (i === undefined) continue;
      const o = 6 * i, [a, b, d] = lv;
      dg[o] += l.w; dg[o + 1] += l.w; dg[o + 2] += l.w;
      dg[o + 3] += l.w * (b * b + d * d) + l.wr; dg[o + 4] += l.w * (a * a + d * d) + l.wr; dg[o + 5] += l.w * (a * a + b * b) + l.wr;
    }
    const b = new Float64Array(6 * n), Ap = new Float64Array(6 * n), r1 = [0, 0, 0, 0, 0, 0], r2 = [0, 0, 0, 0, 0, 0];
    /** The multipliers' rounds (one without ties): solve, then λ += μ·c until the ties' points meet (linearly). */
    const x0 = new Float64Array(6 * n), lam0 = lam.map(l => l.slice());
    const solve = () => {
      let last = Infinity;
      for (let al = 0; al < (T.length ? 40 : 1); al++) {
        applyH(null, b);
        for (let i = 0; i < b.length; i++) b[i] = -b[i];
        // conjugate gradients from the last x, over the unknowns the mask leaves free
        applyH(x, Ap);
        const r = b.map((v, i) => (v - Ap[i]) * mask[i]), z = r.map((v, i) => v / dg[i]), p = z.slice();
        let rz = dot(r, z);
        const tol = 1e-20 * Math.max(1e-30, dot(b, b));
        for (let it = 0; it < 20 * n + 100 && dot(r, r) > tol; it++) {
          applyH(p, Ap);
          const alpha = rz / dot(p, Ap);
          for (let i = 0; i < x.length; i++) { x[i] += alpha * p[i]; r[i] -= alpha * Ap[i] * mask[i]; }
          for (let i = 0; i < z.length; i++) z[i] = r[i] / dg[i];
          const rz1 = dot(r, z), beta = rz1 / rz;
          rz = rz1;
          for (let i = 0; i < p.length; i++) p[i] = z[i] + beta * p[i];
        }
        let worst = 0;
        const cs = T.map(t => {
          const { ia, ib } = tieR(t, x, r1);
          tieR(t, null, r2);
          if (ia === undefined && ib === undefined) return null;   // all held: it stays as it is
          const cd = [r1[0] + r2[0], r1[1] + r2[1], r1[2] + r2[2]];
          worst = Math.max(worst, ...cd.map(Math.abs));
          return cd;
        });
        if (worst < 1e-5) break;
        // ties that cannot all meet (one frame's named points not as far apart as another's) close no further, and
        // λ would only grow, closing them by ever bigger turns on the small difference: the last solve is as near as they come
        if (al > 0 && worst > 0.9 * last) { x.set(x0); lam.forEach((l, k) => l.splice(0, 3, ...lam0[k])); break; }
        last = worst; x0.set(x); lam.forEach((l, k) => lam0[k].splice(0, 3, ...l));
        cs.forEach((cd, k) => { if (cd) for (let d = 0; d < 3; d++) lam[k][d] += mu * cd[d]; });
      }
    };
    x.fill(0);
    mask.fill(1);
    // a frame held at max tilts no further: its tilt set to max the way it went, not solved for
    const hold = (i: number) => {
      const t0 = tau.get(free[i])!, d = held.get(free[i])!;
      mask[6 * i + 3] = mask[6 * i + 4] = 0;
      x[6 * i + 3] = d[0] * tmax - t0[0]; x[6 * i + 4] = d[1] * tmax - t0[1];
    };
    for (let i = 0; i < n; i++) if (!tilts) mask[6 * i + 3] = mask[6 * i + 4] = 0; else if (held.has(free[i])) hold(i);
    for (let k = 0; k < 2 * n + 1; k++) {
      solve();
      // tilted past max: held there, and solved again
      let more = false;
      if (tilts) for (let i = 0; i < n; i++) {
        if (!mask[6 * i + 3]) continue;
        const t0 = tau.get(free[i])!, a = t0[0] + x[6 * i + 3], e = t0[1] + x[6 * i + 4], h = Math.hypot(a, e);
        if (h <= tmax) continue;
        held.set(free[i], [a / h, e / h]); hold(i); more = true;
      }
      if (!more) break;
    }
    // the moves: a turn by ω about the frame's position, then the shift
    if (round === 0) for (const [f, W] of anchors) if (P.has(f)) C.set(f, W);
    free.forEach((f, i) => {
      const o = 6 * i, S = about(rotOf([x[o + 3], x[o + 4], x[o + 5]]), c(f));
      S.t = [S.t[0] + x[o], S.t[1] + x[o + 1], S.t[2] + x[o + 2]];
      C.set(f, C.has(f) ? compose(S, C.get(f)!) : S);
    });
    if (!ties.length || gapNow() < 1e-4) break;
  }
  // the turns' second order may leave a tilt a hair past max: back onto it
  if (tilts) for (const f of free) {
    const t = tauOf(f), h = Math.hypot(t[0], t[1]);
    if (held.has(f) || h > tmax) tilt!.capped?.add(f);
    if (h <= tmax) continue;
    const k = -(h - tmax) / h;
    C.set(f, compose(about(rotOf([t[0] * k, t[1] * k, 0]), moved(f).t), C.get(f)!));
  }
  for (const f of free) {
    const W = C.get(f)!, p = p0.get(f)!.t, q = apply(W, p);
    if (Math.hypot(q[0] - p[0], q[1] - p[1], q[2] - p[2]) < 1e-4 && angleOf(W.R) < 1e-3 * Math.PI / 180) continue;
    out.set(f, W);
  }
  return out;
}
